/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {
  InputField,
  LookupDataOption,
  PlatformDeviceType
} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '../components/menus/Dropdown';
import {DeviceFileFormFields} from './DeviceFileEditorUtils';
import {getLabel} from './helpers';

export type DeviceFileDynamicFieldsProps = {
  onChange?: (deviceType: LookupDataOption) => void;
  readOnly?: boolean;
  label?: string;
  data?: LookupDataOption[];
  field?: InputField;
  isLoading?: boolean;
  required?: boolean;
  hasError?: boolean;
  errorText?: string;
  classes?: string;
  name?: string;
  defaultValue?: string | LookupDataOption[];
  useDisabledStyles?: boolean;
  setFormState?: Function;
}

/**
* Generates the DeviceFile Devicetype chooser dropdown labels and values based on
* the platform devicetypes api
*/
const getOptions = (data): Options[] => {
  return createOptions({
    items     : data,
    valProp   : 'id',
    labelProp : 'name',
    augmentor : (item: PlatformDeviceType, option: { value: string; label: string; }) => {
      return {...option, id: item.id, disabled: false};
    }
  });
};

/**
 * Provides a utility which filters a list of device types for the
 * currently selected value.
 */
const getSelectedValue = (
  optionsList: LookupDataOption[],
  selected: number
): LookupDataOption => {
  return optionsList.find(({id}: LookupDataOption) => id === selected);
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * Gets the value of the selected option (to display). Mainly handles the case when given an array.
 */
const getValue = (selectedOption: string | Options[], isLoading: boolean): string | string[] => {
  if (!isLoading) {
    return Array.isArray(selectedOption) ? selectedOption?.[0]?.label : selectedOption;
  } else {
    return translate('common:states.loading');
  }
};

export enum DEVICE_FILE_FIELD {
  APP_ID = 'appId',
  VBUS = 'vBus'
};

/**
 *
 * The DeviceFileDynamicFields component displays a list of meta data fields based
 * on slected devicetype and filetype
 * ```typescript
 *
 *     import {DeviceFileDynamicFields} from '@ion/dls/src/editors/';
 *
 *     <DeviceFileDynamicFields onChange={setDeviceType} />
 *
 * ```
 *
 * @onChange         Callback prop which will be invoked with the seleted metadata value.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @hasError         When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @name             name for the component that gets passed to the root element.
 */
export const DeviceFileDynamicFields = ({
  label,
  onChange,
  defaultValue,
  name,
  isLoading,
  readOnly,
  required,
  classes,
  data,
  errorText,
  field,
  setFormState,
  hasError = false,
  useDisabledStyles = false
}: DeviceFileDynamicFieldsProps) => {
  const _classes                = useStyles();
  const [selected, setSelected] = useState<string | Options[]>([]);
  const [options, setOptions]   = useState<Options[]>([]);
  const metaDataName            = field?.name as DeviceFileFormFields;

  useEffect(() => {
    if (Array.isArray(defaultValue) && defaultValue?.length) {
      const values: Options[] = defaultValue.map(({id, value, description}) => ({
        label : getLabel(value, description, field),
        value : id,
        id
      }));
      setSelected(values);
    }
    if (typeof defaultValue === 'string' && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue, field]);

  useEffect(() => {
    if (data) {
      const optionsData = data.map(item => {
        return ({
          ...item,
          name : getLabel(item?.value, item?.description, field),
          id   : item?.id
        });
      });
      setOptions(getOptions(optionsData));
      selected && onChange?.(undefined);
    }
  }, [field, data]);

  function handleEmptyMetaData() {
    setFormState({
      [metaDataName] : {
        metaDataName,
        required : true,
        value    : []
      }
    });
  };

  const handleChange = useCallback((event: React.ChangeEvent, child) => {
    const idValues = child?.map(({value}) => value);
    const selectedList = data?.filter(item => {
      return idValues.includes(item?.id);
    });
    setSelected(child);
    if (isEmpty(child)) {
      handleEmptyMetaData();
    } else {
      onChange?.(selectedList);
    }
  }, [data]);

  /**
   * Handles delegations in relation to the parent onChange.
   */
  const handleOnChange = useCallback(({target}: ChangeEvent<HTMLInputElement>): void => {
    const name = target.label as unknown as number;
    setSelected(name);
    onChange(getSelectedValue(data, name));
  }, [data]);

  const isVBus = field?.name === DEVICE_FILE_FIELD.VBUS || field?.name === DEVICE_FILE_FIELD.APP_ID;

  return (
    <div className={classes || _classes.root}>
      {!isVBus ? (
        <Dropdown displaySelectedLabel
          required={required}
          disabled={readOnly || isLoading}
          useDisabledStyles={useDisabledStyles}
          hasError={hasError && required}
          errorText={errorText}
          options={options}
          loading={isLoading}
          value={getValue(selected, isLoading)}
          defaultValue={defaultValue?.[0]?.description}
          fieldLabel={label}
          placeholder={`Select ${label}`}
          preferPlaceholder={!isLoading && !selected}
          onChange={handleOnChange}
          name={name} />
      ) : (
        <Dropdown required={required}
          hasError={hasError && required}
          errorText={errorText}
          disabled={readOnly || isLoading}
          options={options}
          multiSelect
          loading={isLoading}
          value={isLoading ? [translate('common:states.loading')] : selected}
          fieldLabel={label}
          placeholder={`Select ${label}`}
          preferPlaceholder={!isLoading && !selected}
          onChange={function(e, child) { handleChange(e, child); }}
          name={name} />
      )}
    </div>
  );
};

export default DeviceFileDynamicFields;
